import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { Source } from 'src/app/models/source';
import { switchMap } from 'rxjs/internal/operators/switchMap';

// Define a type for analysis types
export type ReportAnalysisType = 'none' | 'analysis' | 'manipulation' | 'ai' | 'metadata' | 'stylometrics' | 'projectSynapse' | 'isOnCohort';

// Define a type for view modes
export type ViewModeType = 'summary' | 'indeepth' | 'ai' | 'charReplacement' | '';

export interface Manipulation {
  includedCharacters: { character: string; count: number }[];
  excludedCharacters: any[];
  characters: any[];
  isExcluded: boolean;
}

export interface Translated {
  lang: string;
  similarity: number;
  percentage: string;
  source: Source;
  isExcluded: boolean;
  comment: string;
  sentence: string;
  secondarySources: any;
}

export interface Data {
  submissionId: string,
  text: string;
  comment: string;
  sentence: string;
  source: Source;
  id: number;
  isOriginal: boolean;
  isTranslated: boolean;
  isManipulated: boolean;
  isExcluded: boolean;
  isAi: boolean;
  similarity: number;
  page: number;
  manipulations: Manipulation[];
  translated: Translated[];
  secondarySources: any;
}

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  constructor(private http: HttpClient) {
  }

  public newS3JsonSubject = new BehaviorSubject<string>('');
  public newS3JsonObject = this.newS3JsonSubject as Observable<string>;

  public filterSourcesSubject = new BehaviorSubject<number>(0);
  public filterSourcesObject = this.filterSourcesSubject as Observable<number>;

  public filterManipulationsSubject = new BehaviorSubject<number>(0);
  public filterManipulationsObject = this.filterManipulationsSubject as Observable<number>;

  public sentenceInformationSubject = new BehaviorSubject<Data | null>(null);
  public sentenceInformationObject = this.sentenceInformationSubject.asObservable();

  public selectedTransltaionCodeSubject = new BehaviorSubject<string | null>(null);
  public selectedTransltaionCodeObject = this.selectedTransltaionCodeSubject.asObservable();

  public documentPercentagesSubject = new BehaviorSubject<{ originalPercentage: number, overallTransltedPercentage: number, originalPercentageWithoutQuotes: number, overallTransltedPercentageWithoutQuotes: number }>
    ({ originalPercentage: 0, overallTransltedPercentage: 0, originalPercentageWithoutQuotes: 0, overallTransltedPercentageWithoutQuotes: 0 });
  public documentPercentagesObject = this.documentPercentagesSubject as Observable<object>;

  public initialSourcesSubject = new BehaviorSubject<any>(null);
  public initialSourcesObject = this.initialSourcesSubject as Observable<any>;

  public isSideBySideVisibleSubject = new BehaviorSubject<boolean>(false);
  public isSideBySideVisible$ = this.isSideBySideVisibleSubject.asObservable();

  private preserveProjectSynapseStateSubject = new BehaviorSubject<boolean>(false);
  preserveProjectSynapseState$ = this.preserveProjectSynapseStateSubject.asObservable();

  // Add a new subject for Project Synapse comparison updates
  private projectSynapseComparisonSubject = new BehaviorSubject<any>(null);
  public projectSynapseComparison$ = this.projectSynapseComparisonSubject.asObservable();

  // Add a new subject for section updates (e.g., when a section is excluded/included)
  public sectionUpdateSubject = new BehaviorSubject<any>(null);
  public sectionUpdate$ = this.sectionUpdateSubject.asObservable();

  // Updated to track both language code and type
  public expandedLanguageSubject = new BehaviorSubject<{ code: string | null, type: string | null }>({ code: null, type: null });
  public expandedLanguageObject = this.expandedLanguageSubject.asObservable();

  // Add subject to track view mode separately from language code
  public viewModeSubject = new BehaviorSubject<ViewModeType>('');
  public viewMode$ = this.viewModeSubject.asObservable();

  public activateFindingSubject = new Subject<{ id: number; page: number; finding?: any }>();
  public activateFinding$ = this.activateFindingSubject.asObservable();

  public activateAISegmentSubject = new Subject<{ id: number; page: number; windowIndex?: number; segment: any }>();
  public activateAISegment$ = this.activateAISegmentSubject.asObservable();

  sentenceInformation(data: Data, viewMode: ViewModeType, languageCode?: string | null, preserveProjectSynapse: boolean = false) {
    this.sentenceInformationSubject.next(data);

    // Update the language code separately from view mode
    // If languageCode is provided, use it; otherwise, only set it if viewMode appears to be a language code
    if (languageCode) {
      this.selectedTransltaionCodeSubject.next(languageCode);
    } else if (viewMode !== 'indeepth' && viewMode !== 'summary' && viewMode !== 'ai' && viewMode) {
      // Backward compatibility for existing calls
      this.selectedTransltaionCodeSubject.next(viewMode);
    }

    // Always track view mode
    this.viewModeSubject.next(viewMode);

    if (preserveProjectSynapse) {
      this.preserveProjectSynapseStateSubject.next(true);

      // Ensure analysis type stays as projectSynapse
      if (this.selectAnalysisTypeSubject.getValue() === 'projectSynapse') {
        setTimeout(() => {
          this.selectAnalysisTypeSubject.next('projectSynapse');
        }, 50);
      }

      setTimeout(() => {
        this.preserveProjectSynapseStateSubject.next(false);
      }, 300);
    }
  }

  public sourcesSubject = new BehaviorSubject<any>(null);
  public sourcesObject = this.sourcesSubject as Observable<any>;
  updateSources(sources: any) {
    this.sourcesSubject.next(sources);
  }

  public selectLanguageSubject = new BehaviorSubject<{
    language: string;
    type: string;
  }>({
    language: 'English',
    type: 'Original',
  });

  public selectLanguageObject = this.selectLanguageSubject as Observable<{
    language: string;
    type: string;
  }>;

  public selectAnalysisTypeSubject = new BehaviorSubject<ReportAnalysisType>('none');
  public selectAnalysisTypeObject = this.selectAnalysisTypeSubject as Observable<ReportAnalysisType>;

  public selectedSourcesSubject = new BehaviorSubject<number[]>([])
  public selectedSourcesObject = this.selectedSourcesSubject as Observable<number[]>

  public toggleQuotesSubject = new BehaviorSubject<boolean>(true);
  public toggleQuotesObject = this.toggleQuotesSubject as Observable<boolean>;

  public toggleAIKeywordsSubject = new BehaviorSubject<boolean>(false);
  public toggleAIKeywordsObject = this.toggleAIKeywordsSubject as Observable<boolean>;

  public aiSentenceAnalysisSubject = new BehaviorSubject<any>(null);
  public aiSentenceAnalysisObject = this.aiSentenceAnalysisSubject as Observable<any>;

  public selectManipulationTypeSubject = new BehaviorSubject<string>('');
  public selectManipulationTypeObject = this.selectManipulationTypeSubject as Observable<string>;

  public currentTranslatedLanguageSubject = new BehaviorSubject<string>('');
  public currentTranslatedLanguageObject = this.currentTranslatedLanguageSubject as Observable<string>;

  filterSources(param: number) {
    this.filterSourcesSubject.next(param);
  }

  submissionDetails(submissionId: any, isOnIA?: boolean, sessionId?: string, skipViewCount?: boolean) {
    const params = new URLSearchParams();
    if (sessionId) params.set('sessionId', sessionId);
    if (skipViewCount) params.set('skipViewCount', '1');
    const queryString = params.toString();
    const queryParams = queryString ? `?${queryString}` : '';

    if (isOnIA) {
      return this.http.get<any>(
        `${environment.apiUrl}/users/submissions/${submissionId}/report${queryParams}`
      );
    } else {
      return this.http.get<any>(
        `${environment.apiUrl}/submission/${submissionId}/report/users${queryParams}`
      );
    }
  }

  documentStatus(submissionId: any) {
    return this.http.get<any>(
      `${environment.apiUrl}/document/${submissionId}/checkStatus`
    );
  }

  getS3Json(url: string) {
    return this.http.get(url).pipe();
  }

  excludeSource(
    sourceId: string,
    submissionId: string,
    language: string
  ) {
    return this.http
      .request(
        'delete',
        `${environment.apiUrl}/submission/${submissionId}/excludeSource/${sourceId}`,
        {
          body: {
            sourceId,
            language
          },
        }
      )
      .pipe();
  }

  includeSource(
    sourceId: string,
    submissionId: string,
    language?: string
  ) {
    return this.http
      .request(
        'put',
        `${environment.apiUrl}/submission/${submissionId}/includeSource/${sourceId}`,
        {
          body: {
            sourceId,
            language
          },
        }
      )
      .pipe();
  }


  downloadOriginalDocument(submissionId: string) {
    return this.http
      .get(`${environment.apiUrl}/document/${submissionId}`)
      .pipe();
  }

  downloadReport(submissionId: string, reportType: string, isOnIA?: boolean, includeSentenceFindings: boolean = false) {
    return this.http
      .post(
        `${environment.apiUrl}/offlineReportGenerator`,
        { submissionId, reportType, bulk: false, includeSentenceFindings }
      )
      .pipe();
  }

  /**
  * Method used to download ai offline pdf report.
  * @param submissionId
  */
  downloadAiReport(submissionId: string) {
    return this.http
      .get(
        `${environment.apiUrl}/submission/${submissionId}/aiOfflineReport?reportType='test'`,
        {}
      )
      .pipe();
  }

  excludeSentence(
    submissionId: string,
    sentenceId: number,
    language: string,
    comment?: string,
    isOnIA?: boolean
  ) {
    let submission;
    if (isOnIA) {
      submission = 'submissions';
    } else {
      submission = 'submission';
    }
    return this.http
      .request(
        'delete',
        `${environment.apiUrl}/${submission}/${submissionId}/sentences/${sentenceId}`,
        {
          body: {
            sentenceId,
            language,
            comment
          }
        }
      )
      .pipe();
  }

  includeSentence(
    submissionId: string,
    sentenceId: number,
    language: string,
    comment?: string,
    isOnIA?: boolean
  ) {
    let submission;
    if (isOnIA) {
      submission = 'submissions';
    } else {
      submission = 'submission';
    }
    return this.http
      .request(
        'put',
        `${environment.apiUrl}/${submission}/${submissionId}/sentences/${sentenceId}`,
        {
          body: {
            sentenceId,
            language
          }
        }
      )
      .pipe();
  }

  resetReportToOriginal(submissionId: string) {
    return this.http.post(
      `${environment.apiUrl}/submission/${submissionId}/report/reset`,
      {}
    ).pipe();
  }

  changeSentenceSource(
    sourceId: string,
    sentenceId: number,
    submissionId: string,
    language?: string
  ) {
    return this.http.post<any>(
      `${environment.apiUrl}/submission/${submissionId}/source/${sourceId}`,
      { sentenceId, language }
    );
  }

  sentContentRequest(
    sourceId: string,
    submissionId,
    description: string,
    index,
    sourceSubmissionId: string,
    institutionId?: string
  ) {
    return this.http.post<any>(
      `${environment.apiUrl}/submission/${submissionId}/request/content`,
      { sourceId, description, index, sourceSubmissionId, institutionId }
    );
  }

  getContentRequest() {
    return this.http.get<any>(
      `${environment.apiUrl}/requests/content`
    );
  }

  changeContentRequestAccess(requestId, status) {
    return this.http.post<any>(
      `${environment.apiUrl}/request/content/${requestId}`, { status }
    );
  }

  updateComment(
    comment: string,
    submissionId: string,
    sentenceId: number,
    language: string
  ) {
    return this.http
      .put<any>(
        `${environment.apiUrl}/submission/${submissionId}/sentences/${sentenceId}/updateComment`,
        { language, comment }).pipe()
  }

  submissionPreview(sourceId: string, submissionId: string, index: string) {
    return this.http.post<any>(
      `${environment.apiUrl}/submission/${submissionId}/preview/${sourceId}`,
      { index }
    );
  }

  toggleReplacedCharacters(submissionId: string, charsToToggle: any, type: number) {
    return this.http
      .request(
        'put',
        `${environment.apiUrl}/submission/${submissionId}/toggle/1`,
        { body: { charsToToggle, exclude: type == 1 ? true : false } }
      )
      .pipe();
  }

  toggleWhiteCharacterSentences(submissionId: string, sentenceId: number, excludeSentence: boolean) {

    return this.http
      .request(
        'put',
        `${environment.apiUrl}/submission/${submissionId}/toggle/2`,
        { body: { sentenceId, excludeSentence } }
      )
      .pipe();
  }

  toggleManipulatedImages(submissionId: string, imageId: number, exclude: boolean) {

    return this.http
      .request(
        'put',
        `${environment.apiUrl}/submission/${submissionId}/toggle/3`,
        { body: { imageId, exclude } }
      )
      .pipe();
  }

  getLLMAnalysis(uploader_name: string, submission_id: string, isOnIA?: boolean, anonymous?: boolean) {
    const getCurrentFormattedDate = () => {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0'); // Add leading zero if needed
      const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    return this.http.post<any>(
      `${environment.apiUrl}/getLLMAnalysis`,
      { assignment_date: getCurrentFormattedDate(), uploader_name, submission_id, anonymous }
    );
  }

  updateLLMAnalysis(submission_id: string, uploader_name: string, language: string, content: string) {
    return this.http.post<any>(
      `${environment.apiUrl}/updateLLMAnalysis`,
      { submission_id, uploader_name, language, content }
    );
  }

  toggleMetadataFlag(submission_id: string, uploader_name: string, key: string, exclude: boolean) {
    return this.http.post<any>(
      `${environment.apiUrl}/toggleMetadataFlag`,
      { submission_id, uploader_name, key, exclude }
    );
  }

  preserveProjectSynapseState(shouldPreserve: boolean): void {
    this.preserveProjectSynapseStateSubject.next(shouldPreserve);
  }

  /**
   * Updates the Project Synapse comparison data and notifies subscribers
   * This is used to ensure all components are aware of model changes
   * @param comparisonData The updated comparison data
   */
  updateProjectSynapseComparison(comparisonData: any): void {
    if (comparisonData) {
      this.projectSynapseComparisonSubject.next({
        comparison: comparisonData,
        timestamp: new Date().getTime()
      });
    }
  }


  getReport(submissionId: string) {
    return this.submissionDetails(submissionId).pipe(
      switchMap((details) => {
        const url = details?.jsonUrl;
        return this.getS3Json(url);
      })
    );
  }

}
